/**
 * Версии плановых данных БДР (Р-14, ТЗ §4.8) — экран управления версиями плана.
 *
 * Список версий модуля с отметкой активной; создание новой версии (черновик)
 * со строками плана (JSON-массив); активация (утверждение) и удаление черновика.
 * Активная версия — база план/факт новых прогонов; утверждённые прогоны при
 * смене активной НЕ пересчитываются. При can_write=false — только просмотр.
 */
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ReadOnlyBanner, actionRefusal, useActionPermission } from '@entities/user';
import { apiErrorMessage, useEconPermission } from '@entities/economic/types';
import type { EconPlanVersion } from '@shared/api';
import {
  useActivateEconPlanVersionMutation,
  useCreateEconPlanVersionMutation,
  useDeleteEconPlanVersionMutation,
  useGetEconObjectEconomyQuery,
  useGetEconPlanVersionQuery,
  useListEconPlanVersionsQuery,
  useListPtoProjectsQuery,
  type NewEconPlanLine,
} from '@shared/api';
import { useActiveProjectContext } from '@shared/lib/activeProjectContext';
import {
  VisualPlanEditor,
  PlanVersionLinesTable,
  buildPlanLinesFromEditor,
  createEmptyPlanRow,
  type PlanEditorRow,
} from './VisualPlanEditor';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;
const Th = styled.th`
  text-align: left;
  padding: 8px;
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  font-weight: 600;
`;
const Td = styled.td`
  padding: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  vertical-align: top;
`;
const Badge = styled.span<{ $active: boolean }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 12px;
  background: ${({ $active, theme }) => ($active ? theme.colors.primary : 'transparent')};
  color: ${({ $active, theme }) => ($active ? '#fff' : theme.colors.text.primary)};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;
const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 880px;
`;
const Input = styled.input`
  padding: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;
`;
const Select = styled.select`
  padding: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;
`;
const BaselineBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.surface};
`;
const JsonArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  font-family: monospace;
  font-size: 13px;
  padding: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
`;
const Advanced = styled.details`
  & > summary {
    cursor: pointer;
    font-size: 13px;
    opacity: 0.8;
  }
`;
const Hint = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;
const Err = styled.div`
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
`;

const PLACEHOLDER = `[
  {"object":"ОЗХ","metric":"revenue","plan_value":1000000,"period_month":"2026-07"},
  {"object":"ОЗХ","metric":"cost","cost_article":"материалы","plan_value":300000,"period_month":"2026-07"},
  {"object":"ОЗХ","metric":"man_hours","plan_value":1600,"period_month":"2026-07"}
]`;

/** Активация версии плана — утверждение: право-действие `econ.bdr.approve`
 *  (ТЗ п. 6.10, Д8). Автор свою ещё не утверждённую версию не утверждает. */
const PLAN_APPROVAL_HINT = 'Утверждает главный экономист или руководитель';
const OWN_PLAN_HINT = 'Свою версию плана утверждает другой сотрудник с правом утверждения';

export function PlanVersionsView() {
  const { can_write } = useEconPermission();
  const approval = useActionPermission('econ.bdr.approve');
  const { activeProjectId, setActiveProjectId } = useActiveProjectContext();
  const [projectId, setProjectId] = useState(activeProjectId ?? '');
  const [label, setLabel] = useState('');
  const [rows, setRows] = useState<PlanEditorRow[]>([createEmptyPlanRow()]);
  const [advancedJson, setAdvancedJson] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [listErr, setListErr] = useState<string | null>(null);
  const [baselineNotice, setBaselineNotice] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    if (activeProjectId && !projectId) {
      setProjectId(activeProjectId);
    }
  }, [activeProjectId, projectId]);

  const { data: projectsData } = useListPtoProjectsQuery();
  const projects = projectsData?.items ?? [];
  const { data: econObject } = useGetEconObjectEconomyQuery(
    { project_id: projectId || undefined },
    { skip: !projectId },
  );

  const { data, isLoading } = useListEconPlanVersionsQuery({ module: 'bdr' });
  const { data: detail } = useGetEconPlanVersionQuery(selected ?? '', { skip: !selected });
  const [createVersion, { isLoading: creating }] = useCreateEconPlanVersionMutation();
  const [activate] = useActivateEconPlanVersionMutation();
  const [remove] = useDeleteEconPlanVersionMutation();

  const versions = data?.versions ?? [];

  /** Причина, по которой версию нельзя активировать (зеркало бэкенда: уже
   *  утверждённую версию повторно делает активной любой обладатель права). */
  function activationRefusal(v: EconPlanVersion) {
    return actionRefusal(approval, v.approved_at ? null : v.created_by);
  }

  function fillBaselineFromProject() {
    setErr(null);
    setBaselineNotice(null);
    const proj = projects.find((p) => p.id === projectId);
    const objName = econObject?.object_name || proj?.code || proj?.name || 'Объект';
    const baseRevenue =
      econObject && econObject.contract_price > 0
        ? Math.round(econObject.contract_price)
        : econObject && econObject.ks2_total > 0
          ? Math.round(econObject.ks2_total)
          : 0;
    const periodMonth = new Date().toISOString().slice(0, 7);

    if (baseRevenue <= 0) {
      setErr('По выбранному объекту не найдена договорная цена или смета для автозаполнения.');
      return;
    }

    const matCost = Math.round(baseRevenue * 0.45);
    const laborCost = Math.round(baseRevenue * 0.25);
    const mechCost = Math.round(baseRevenue * 0.15);

    const generatedRows: PlanEditorRow[] = [
      {
        object: objName,
        metric: 'revenue',
        costArticle: '',
        planValue: String(baseRevenue),
        periodMonth,
        projectId,
      },
      {
        object: objName,
        metric: 'cost',
        costArticle: 'материалы',
        planValue: String(matCost),
        periodMonth,
        projectId,
      },
      {
        object: objName,
        metric: 'cost',
        costArticle: 'оплата труда',
        planValue: String(laborCost),
        periodMonth,
        projectId,
      },
      {
        object: objName,
        metric: 'cost',
        costArticle: 'механизмы и субподряд',
        planValue: String(mechCost),
        periodMonth,
        projectId,
      },
    ];
    setRows(generatedRows);
    if (!label.trim()) {
      setLabel(`Базовый план БДР · ${objName} (${periodMonth})`);
    }
    setBaselineNotice(
      `Заполнено 4 базовые плановые строки из финансового паспорта/сметы объекта «${objName}» (лимит: ${baseRevenue.toLocaleString('ru-RU')} ₽).`,
    );
  }

  async function submit() {
    setErr(null);
    if (!label.trim()) {
      setErr('Укажите метку версии');
      return;
    }
    let lines: NewEconPlanLine[];
    if (advancedJson.trim()) {
      try {
        const parsed = JSON.parse(advancedJson);
        if (!Array.isArray(parsed)) throw new Error('ожидался массив строк');
        lines = parsed as NewEconPlanLine[];
      } catch (e) {
        setErr(`Невалидный JSON строк плана: ${(e as Error).message}`);
        return;
      }
    } else {
      const result = buildPlanLinesFromEditor(rows);
      if (result.error) {
        setErr(result.error);
        return;
      }
      lines = result.lines;
    }
    try {
      await createVersion({ module: 'bdr', label, lines }).unwrap();
      setLabel('');
      setRows([createEmptyPlanRow()]);
      setAdvancedJson('');
    } catch (e) {
      // Backend объясняет причину по-русски с номером строки (месяц, статья,
      // объект ОСНОВЫ) — показываем её, а не сырой JSON ошибки.
      setErr(apiErrorMessage(e) ?? `Ошибка создания: ${JSON.stringify(e)}`);
    }
  }

  async function activateVersion(id: string) {
    setListErr(null);
    try {
      await activate(id).unwrap();
    } catch (e) {
      setListErr(apiErrorMessage(e) ?? 'Не удалось активировать версию плана.');
    }
  }

  async function removeVersion(id: string) {
    setListErr(null);
    try {
      await remove(id).unwrap();
      if (selected === id) setSelected(null);
    } catch (e) {
      setListErr(apiErrorMessage(e) ?? 'Не удалось удалить версию плана.');
    }
  }

  return (
    <Wrap>
      {!can_write && <ReadOnlyBanner />}
      <p style={{ margin: 0, fontSize: 13, opacity: 0.8 }}>
        Версии плана БДР (Р-14). Активная версия — база план/факт новых прогонов; уже
        утверждённые прогоны при смене активной версии не пересчитываются.
      </p>
      <Hint>
        План считается по месяцам: в расчёт месяца идут только строки активной версии за этот
        месяц. Загруженный в прогон «План по объекту» применяется, только если в версии на этот
        месяц нет строк, — план не складывается дважды.
      </Hint>
      {listErr && <Err role="alert">{listErr}</Err>}

      {isLoading ? (
        <div>Загрузка…</div>
      ) : versions.length === 0 ? (
        <div style={{ opacity: 0.7 }}>Версий плана нет. Создайте первую ниже.</div>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Метка</Th>
              <Th>Статус</Th>
              <Th>Утверждена</Th>
              <Th>Создана</Th>
              <Th>Действия</Th>
            </tr>
          </thead>
          <tbody>
            {versions.map((v) => (
              <tr key={v.id}>
                <Td>
                  <button
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    onClick={() => setSelected(selected === v.id ? null : v.id)}
                  >
                    {v.label}
                  </button>
                </Td>
                <Td>
                  <Badge $active={v.is_active}>{v.is_active ? 'активная' : 'черновик'}</Badge>
                </Td>
                <Td>{v.approved_at ? new Date(v.approved_at).toLocaleDateString('ru') : '—'}</Td>
                <Td>{new Date(v.created_at).toLocaleDateString('ru')}</Td>
                <Td>
                  {can_write && !v.is_active && (
                    <>
                      <button
                        type="button"
                        disabled={activationRefusal(v) !== null}
                        title={
                          activationRefusal(v) === 'own'
                            ? OWN_PLAN_HINT
                            : activationRefusal(v) === 'missing'
                              ? PLAN_APPROVAL_HINT
                              : undefined
                        }
                        onClick={() => void activateVersion(v.id)}
                      >
                        Активировать
                      </button>{' '}
                      <button onClick={() => void removeVersion(v.id)}>Удалить</button>
                    </>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {selected && detail && (
        <div>
          <h4 style={{ margin: '8px 0' }}>Строки версии «{detail.version?.label}»</h4>
          <PlanVersionLinesTable lines={detail.lines} projects={projects} />
        </div>
      )}

      {can_write && (
        <Form>
          <h3 style={{ margin: 0 }}>Создать версию плана</h3>
          <BaselineBar>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Объект для базового плана:</span>
            <Select
              value={projectId}
              onChange={(e) => {
                const v = e.target.value;
                setProjectId(v);
                setActiveProjectId(v || null);
              }}
            >
              <option value="">— выберите объект —</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code ? `${p.code} — ${p.name}` : p.name}
                </option>
              ))}
            </Select>
            <button
              type="button"
              disabled={!projectId}
              onClick={fillBaselineFromProject}
            >
              ⚡ Заполнить базовый план из договора/сметы объекта
            </button>
            {baselineNotice && <Hint>{baselineNotice}</Hint>}
          </BaselineBar>
          <Input
            placeholder="Метка версии (например «v1 корректировка Q3»)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
          <VisualPlanEditor rows={rows} onChange={setRows} projects={projects} />

          <Advanced>
            <summary>Расширенный режим (JSON)</summary>
            <Hint style={{ margin: '8px 0' }}>
              Если заполнено, используется вместо таблицы выше. Массив объектов вида{' '}
              {'{ object, metric, cost_article, plan_value, period_month, project_id, contract_id }'}
              ; месяц period_month (ГГГГ-ММ) обязателен.
            </Hint>
            <JsonArea
              placeholder={PLACEHOLDER}
              value={advancedJson}
              onChange={(e) => setAdvancedJson(e.target.value)}
            />
          </Advanced>

          {err && <Err>{err}</Err>}
          <button onClick={submit} disabled={creating || !label.trim()}>
            {creating ? 'Создание…' : 'Создать черновик'}
          </button>
          <Hint>
            Версия создаётся черновиком; активируйте её, чтобы применить к новым прогонам.
          </Hint>
        </Form>
      )}
    </Wrap>
  );
}
